/**
 * Sound Notification Service
 * Provides audio alerts for various events in the application
 */

// Sound URLs - using data URIs for reliable playback
const SOUNDS = {
  newOrder: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVY2n+Xm4Wd9UR5D3/fw4rqzj1xR',
  alert: 'data:audio/wav;base64,UklGRl9vT19teleXjF5faaQAf//IAAgASRIAAEAAMASQBJAEYAVQBYAF8AYgBmAGkAawBtAG8AcABxAHIAcwB0AHUAdgB3AHgAeQB6AHsAfAB9AH4AfwCAAIEAggCDAIQAhQCGAIcAiACJAIoAiwCMAI0AjgCPAJAAkQCSAJMAlACVAJYAlwCYAJkAmgCbAJwAnQCeAJ8AoAChAKIAowCkAKUApgCnAKgAqQCqAKsArACtAK4ArwCwALEAsgCzALQAtQC2ALcAuAC5ALoAuwC8AL0AvgC/AMAAwQDCAMMAHABQAA==',
  success: 'data:audio/wav;base64,UklGRl9vT19TAAgASRIAAEAAMASQBJAEYAVQBYAF8AaABmAGkAbgBwAHEAcgBzAHQAdgB3AHgAeQB6AHsAfAB9AH4AfwCAAIEAggCDAIQAhQCGAIcAiACJAIoAiwCMAI0AjgCPAJAAkQCSAJMAlACVAJYAlwCYAJkAmgCbAJwAnQCeAJ8AoAChAKIAowCkAKUApgCnAKgAqQCqAKsArACtAK4ArwCwALEAsgCzALQAtQC2ALcAuAC5ALoAuwC8AL0AvgC/A==',
  warning: 'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAAABAAEARACIQAEQBCAIgAEgCQAJQAmACcAKAApACoAKwAsAC0ALgAvADAAMQAyADMANAA1ADYANwA4ADkAOgA7ADwAPQA+AD8AQABBAEIARABBAEAAPQA8ADsAOgA5ADgANwA2ADUANAA==',
  message: 'data:audio/wav;base64,UklGRiQDAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQADAACAgICAgICBgYKDhIWGiImKi42Oj5GSk5WXmJqbnZ+ho6Wnqausrq+xsrS1tre4uLm5uLi3trWzsrCvraylpKGfnJqXlZKQjYuIhoOBgH9+fXx7enl4eHh4eHl5e3x+f4GDhYeKjI+Rk5aYm52foqSnqautrrCxs7S1tre3t7e2tbSysK6sq6inpaOhn52bmZeVk5GQjo2Mi4uKioqLi4yNjo+QkZKUlZaXmJmam5ydnp+goaKio6OjoqKhoJ+enZybmpmYl5aVlJOSkZCPjoyMi4qJiIeGhYSEg4KBgYCAgA=='
};

class SoundNotificationService {
  constructor() {
    this.enabled = localStorage.getItem('soundNotifications') !== 'false';
    this.volume = parseFloat(localStorage.getItem('soundVolume') || '0.5');
    this.audioContext = null;
  }

  init() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported');
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('soundNotifications', enabled.toString());
  }

  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('soundVolume', this.volume.toString());
  }

  async play(soundType) {
    if (!this.enabled) return;

    try {
      const soundData = SOUNDS[soundType] || SOUNDS.alert;
      const audio = new Audio(soundData);
      audio.volume = this.volume;
      await audio.play();
    } catch (error) {
      // Fallback to oscillator-based sound
      this.playOscillatorSound(soundType);
    }
  }

  playOscillatorSound(soundType) {
    if (!this.audioContext) this.init();
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Different frequencies for different sound types
      const frequencies = {
        newOrder: [523.25, 659.25, 783.99], // C5, E5, G5 - happy chord
        alert: [440, 880], // A4, A5 - attention
        success: [523.25, 783.99], // C5, G5 - positive
        warning: [349.23, 440], // F4, A4 - cautious
        message: [587.33, 783.99] // D5, G5 - friendly notification
      };

      const freqs = frequencies[soundType] || frequencies.alert;
      
      gainNode.gain.setValueAtTime(this.volume * 0.3, this.audioContext.currentTime);
      
      freqs.forEach((freq, i) => {
        setTimeout(() => {
          const osc = this.audioContext.createOscillator();
          const gain = this.audioContext.createGain();
          
          osc.connect(gain);
          gain.connect(this.audioContext.destination);
          
          osc.frequency.setValueAtTime(freq, this.audioContext.currentTime);
          osc.type = 'sine';
          
          gain.gain.setValueAtTime(this.volume * 0.2, this.audioContext.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
          
          osc.start(this.audioContext.currentTime);
          osc.stop(this.audioContext.currentTime + 0.3);
        }, i * 150);
      });
    } catch (e) {
      console.warn('Could not play sound', e);
    }
  }

  // Convenience methods
  playNewOrder() {
    this.play('newOrder');
  }

  playAlert() {
    this.play('alert');
  }

  playSuccess() {
    this.play('success');
  }

  playWarning() {
    this.play('warning');
  }

  playMessage() {
    this.play('message');
  }
}

// Singleton instance
export const soundService = new SoundNotificationService();

// React hook for sound notifications
export const useSoundNotification = () => {
  return {
    play: (type) => soundService.play(type),
    playNewOrder: () => soundService.playNewOrder(),
    playAlert: () => soundService.playAlert(),
    playSuccess: () => soundService.playSuccess(),
    playWarning: () => soundService.playWarning(),
    playMessage: () => soundService.playMessage(),
    setEnabled: (enabled) => soundService.setEnabled(enabled),
    setVolume: (volume) => soundService.setVolume(volume),
    isEnabled: () => soundService.enabled,
    getVolume: () => soundService.volume
  };
};
